/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import axios from 'axios';
import env from '../../../../common/env';
import utils from '../../../../common/utils';
import runGraphService from '../../../studio/services/graphs/RunGraphService';
import parseRunGraphStreamToText from '../../../studio/vibeflow/services/parseRunGraphStreamToText';
import MAVEN from './mavenConfig';
import { addStep, readRequestId, readStreamPayload } from './mavenParse';

/**
 * Runs the Maven graph for one question, the same way Studio's Test Graph does: runGraphService
 * (which sends useStream: true) and the shared Studio stream reader. Maven only adds progress steps.
 * There is no thread: the last few turns go in `history`, so Maven can follow up on "yes, go ahead".
 *
 * @param {{ question: string, pageContext?: object, history?: Array<{ question: string, answer: string }>,
 *   onProgress?: (p: { text: string, steps: string[] }) => void,
 *   signal?: AbortSignal }} params
 * @returns {Promise<{ answer: string, requestId: string|null, steps: string[] }>}
 */
export const askQuestion = async ({ question, pageContext, history, onProgress, signal }) => {
  // No requestId in the payload: berlin-api names the run itself and returns the id in a response header.
  const payload = {
    input: question,
    ...(pageContext ? { pageContext } : {}),
    ...(history?.length ? { history } : {})
  };
  const response = await runGraphService({
    graphId: MAVEN.graphId,
    graphVersion: MAVEN.version,
    tenantId: MAVEN.tenantId,
    payload,
    signal
  });
  const requestId = readRequestId(response);

  let steps = [];
  let failure = null;
  let shownText = '';
  // Where the answer starts in the streamed text. Text written before a step (for example "I'll find your
  // top books." next to a tool call) is not the answer, so each step moves the start past it.
  let answerStart = 0;
  const streamed = await parseRunGraphStreamToText(response, 'The graph returned an empty answer.', {
    allowEmpty: true,
    onEvent: (event, textSoFar) => {
      const progress = readStreamPayload(event);
      if (progress?.error) failure = progress.error;
      if (progress?.step) {
        steps = addStep(steps, progress.step);
        answerStart = textSoFar.length;
      }
      const text = textSoFar.slice(answerStart);
      if (progress?.step || text !== shownText) {
        shownText = text;
        onProgress?.({ text, steps });
      }
    }
  });
  const answer = streamed.slice(answerStart);

  if (failure) throw new Error(failure);
  if (!answer.trim()) throw new Error('The graph returned an empty answer.');
  return { answer, requestId, steps };
};

/**
 * Asks ai-admin-services to check the answer: did its SQL really run, do its proof ids exist,
 * are its numbers in the results? It reads what the question ran from berlin-api's logs, by request id.
 * @param {string} answer
 * @param {string|null} requestId - the run's id that askQuestion read from the response
 * @returns {Promise<{ status: string, requestIds: object[], numbers: object, warnings: string[] }>}
 */
export const verifyAnswer = async (answer, requestId) => {
  if (!requestId) throw new Error('No request id came back from the graph run, so the answer cannot be checked.');
  const { data } = await axios.post(
    `${env.AI_ADMIN_API_BASE_URL}/maven/verify`,
    { answer, requestId },
    { headers: { Authorization: `Bearer ${utils.getToken()}`, 'Content-Type': 'application/json' } }
  );
  return data;
};
